import { motion, useReducedMotion, type Variants } from "framer-motion";
import { forwardRef, type CSSProperties, type ReactNode } from "react";

/**
 * Wrap a list/grid in <StaggerList> and each direct child in <StaggerItem>
 * to get a soft cascade-in: each item fades + slides up 6px with a ~45ms
 * stagger between siblings. Plays once on mount. Respects
 * prefers-reduced-motion.
 *
 * Standard "page arriving" motion for v2 -- use whenever a fresh page
 * renders a collection (chapter rows, subject grid, dashboard cards,
 * study mode tiles).
 *
 * Always renders as a <div>. If you need <ul> semantics, set
 * role="list" / role="listitem".
 */

type MotionDivProps = {
  className?: string;
  style?: CSSProperties;
  role?: string;
  id?: string;
  "aria-label"?: string;
  children?: ReactNode;
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] },
  },
};

type StaggerListProps = MotionDivProps & {
  /** Delay (in seconds) between successive children. Default 0.045s. */
  gap?: number;
  /** Initial delay before the first child animates. Default 0.04s. */
  startDelay?: number;
};

export const StaggerList = forwardRef<HTMLDivElement, StaggerListProps>(
  ({ children, gap, startDelay, ...rest }, ref) => {
    const reduce = useReducedMotion();
    if (reduce) {
      return (
        <div ref={ref} {...rest}>
          {children}
        </div>
      );
    }
    const variants: Variants = {
      hidden: { opacity: 1 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: gap ?? 0.045,
          delayChildren: startDelay ?? 0.04,
        },
      },
    };
    return (
      <motion.div
        ref={ref}
        initial="hidden"
        animate="visible"
        variants={variants}
        {...rest}
      >
        {children}
      </motion.div>
    );
  },
);
StaggerList.displayName = "StaggerList";

export const StaggerItem = forwardRef<HTMLDivElement, MotionDivProps>(
  ({ children, ...rest }, ref) => {
    const reduce = useReducedMotion();
    if (reduce) {
      return (
        <div ref={ref} {...rest}>
          {children}
        </div>
      );
    }
    return (
      <motion.div ref={ref} variants={itemVariants} {...rest}>
        {children}
      </motion.div>
    );
  },
);
StaggerItem.displayName = "StaggerItem";

/**
 * Simple single-element fade-in. For headlines, banners, or any one-off
 * element on a page that should arrive softly.
 */
export function FadeIn({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  if (reduce) {
    return <div className={className}>{children}</div>;
  }
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}
